import { useState } from 'react';
import type { CSSProperties } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import * as monthUtils from '@actual-app/core/shared/months';
import { currencyToInteger } from '@actual-app/core/shared/util';
import type { IntegerAmount } from '@actual-app/core/shared/util';
import type { CategoryEntity } from '@actual-app/core/types/models';

import { BotonCerrarHoja } from '#components/common/ModalHoja';
import { METAS, nombreCofre } from '#components/mobile/travesia/catalogo';
import type { IdMeta } from '#components/mobile/travesia/catalogo';
import { DibujoMeta } from '#components/mobile/travesia/DibujosMetas';
import { Boton } from '#components/mobile/ui/Boton';
import { CampoImporte } from '#components/mobile/ui/CampoImporte';
import { separarEmoji } from '#components/mobile/ui/emoji';
import { Hoja } from '#components/mobile/ui/Hoja';
import { Segmentado } from '#components/mobile/ui/Segmentado';
import { color, radio, TACTIL, texto } from '#components/mobile/ui/tokens';
import { useCategory } from '#hooks/useCategory';
import { useFormat } from '#hooks/useFormat';
import { addNotification } from '#notifications/notificationsSlice';
import { useDispatch, useSelector } from '#redux';

import {
  formularioDesde,
  objetivoDesde,
  useGuardarObjetivo,
} from './ObjetivoPage';
import type { Formulario } from './ObjetivoPage';
import { lineasDeObjetivo } from './objetivos';
import { claseDesdeTravesia, esObjetivoConFecha } from './recuerdos';
import { cartaVacia, SeccionCarta } from './SeccionCarta';
import type { DatosCarta } from './SeccionCarta';
import { firmaLimpia, metaCambiada } from './travesias';
import { usePagadas } from './usePagadas';
import { useAccionesRecuerdo } from './useRecuerdos';
import {
  useBarcoCompartido,
  useTravesiaCategoria,
} from './useTravesiaCategoria';
import type { TravesiaCategoria } from './useTravesiaCategoria';

type HojaZarparProps = {
  categoryId: string;
  /** Mes que se está viendo (`AAAA-MM`). */
  month: string;
  onCerrar: () => void;
  /** «Cerrar la travesía» (solo si ya navega). */
  onCerrarTravesia?: (categoryId: string) => void;
  /** Para tests y capturas: fuerza el barco compartido. */
  compartido?: boolean;
  /** Primer zarpe (ver `zarpe` en el modal). */
  zarpe?: boolean;
};

/** Lo que la ayuda de Gavi enseña ya escrito en la hoja de ejemplo. */
export type RellenoZarpar = {
  importe: IntegerAmount;
  /** `AAAA-MM-DD`. */
  fecha: string;
  clase: IdMeta;
  carta: string;
  siEntonces: string;
};

const estiloCampo: CSSProperties = {
  width: '100%',
  minHeight: TACTIL + 4,
  borderRadius: radio.boton,
  backgroundColor: color.surface2,
  border: `1px solid ${color.line}`,
  padding: '0 14px',
  fontSize: 16,
  color: color.fg,
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  outline: 'none',
};

const estiloEtiqueta: CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 700,
  color: color.fg2,
  margin: '0 2px 6px',
};

async function leerCelda(name: string): Promise<number> {
  try {
    const celda = await send('get-cell', {
      sheetName: monthUtils.sheetForMonth(monthUtils.currentMonth()),
      name,
    });
    return typeof celda?.value === 'number' ? celda.value : 0;
  } catch {
    return 0;
  }
}

/**
 * «Zarpar» (F1 §2, «Al crear la travesía»): la meta con fecha de una
 * categoría (una vez o cada año, importe y fecha), su tipo de meta (los cinco
 * dibujos) y la carta. Si la travesía ya navega, edita su carta. Al guardar:
 * escribe el objetivo en la nota, zarpa y avisa («Zarpas con 120 € a
 * bordo» si ya había saldo).
 */
export function HojaZarpar(props: HojaZarparProps) {
  const { data: category } = useCategory(props.categoryId);
  const datos = useTravesiaCategoria(category);
  if (!category) {
    return null;
  }
  // Si el objetivo o la travesía llegan después (consultas), se rehace.
  // En el primer zarpe, la adopción del detector no rehace el formulario
  // (no se pierde lo que se está escribiendo).
  const clave = `${JSON.stringify(datos.objetivo)}|${props.zarpe ? '' : (datos.recuerdo?.base.rid ?? '')}`;
  return (
    <FormularioZarpar
      key={clave}
      {...props}
      category={category}
      datos={datos}
    />
  );
}

/**
 * La hoja de zarpar de ejemplo para la ayuda de Gavi en Ahorros: una
 * travesía que no existe, vacía o ya escrita (`relleno`). «Zarpar» no hace
 * nada: no se crea ni se guarda nada.
 */
export function HojaZarparEjemplo({
  nombre,
  month,
  relleno,
  onCerrar,
}: {
  nombre: string;
  month: string;
  relleno: RellenoZarpar | null;
  onCerrar: () => void;
}) {
  return (
    <FormularioZarpar
      key={relleno ? 'lleno' : 'vacio'}
      categoryId={ID_EJEMPLO}
      month={month}
      onCerrar={onCerrar}
      zarpe
      compartido={false}
      category={{ id: ID_EJEMPLO, name: nombre, group: '' }}
      datos={{
        objetivo: null,
        esFijo: false,
        tipo: 'travesia',
        recuerdo: null,
      }}
      ejemplo={relleno ?? true}
    />
  );
}

const ID_EJEMPLO = 'ayuda-ejemplo';

function FormularioZarpar({
  categoryId,
  month,
  onCerrar,
  onCerrarTravesia,
  compartido: compartidoForzado,
  zarpe = false,
  category,
  datos: { objetivo, esFijo, recuerdo },
  ejemplo,
}: HojaZarparProps & {
  category: CategoryEntity;
  datos: TravesiaCategoria;
  /** Hoja de ejemplo de la ayuda (vacía con `true`): no guarda nada. */
  ejemplo?: RellenoZarpar | true;
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const format = useFormat();
  const compartidoBarco = useBarcoCompartido();
  const compartido = compartidoForzado ?? compartidoBarco;
  const rawTravesia = useSelector(state => state.prefs.synced.travesia);
  const { guardar, guardando } = useGuardarObjetivo(categoryId, month);
  const { setFijo } = usePagadas(month);
  const acciones = useAccionesRecuerdo();

  const hoy = monthUtils.currentDay();
  const relleno = ejemplo === true ? null : (ejemplo ?? null);
  const [formulario, setFormulario] = useState<Formulario>(() => {
    const f = formularioDesde(
      relleno
        ? { tipo: 'una-vez', importe: relleno.importe, fecha: relleno.fecha }
        : esObjetivoConFecha(objetivo)
          ? objetivo
          : null,
      month,
      format.forEdit,
    );
    return f.tipo === 'anual' || f.tipo === 'una-vez'
      ? f
      : { ...f, tipo: 'una-vez' };
  });
  const [clase, setClase] = useState<IdMeta>(
    () =>
      relleno?.clase ??
      recuerdo?.base.clase ??
      claseDesdeTravesia(categoryId, rawTravesia),
  );
  const [carta, setCarta] = useState<DatosCarta>(() => {
    const vacia = cartaVacia(formulario.fecha);
    if (relleno) {
      return {
        ...vacia,
        carta: relleno.carta,
        siEntonces: relleno.siEntonces,
      };
    }
    if (!recuerdo) {
      return vacia;
    }
    const mia = recuerdo.aportes[acciones.disp]?.firma ?? '';
    return {
      ...vacia,
      carta: recuerdo.base.carta,
      siEntonces: recuerdo.base.siEntonces,
      abrir: recuerdo.base.abrirDesde ? 'hasta' : 'llegar',
      abrirDesde: recuerdo.base.abrirDesde ?? vacia.abrirDesde,
      firma: mia,
      firmando: !!mia,
    };
  });
  const [ocupado, setOcupado] = useState(false);

  const cambiar = (cambios: Partial<Formulario>) =>
    setFormulario(prev => ({ ...prev, ...cambios }));
  const nuevo = objetivoDesde(formulario);
  const fechaPasada =
    nuevo != null &&
    esObjetivoConFecha(nuevo) &&
    nuevo.fecha.slice(0, 7) < month;
  const navega = !!recuerdo && !zarpe;
  const puedeGuardar =
    nuevo != null &&
    esObjetivoConFecha(nuevo) &&
    !fechaPasada &&
    !guardando &&
    !ocupado &&
    acciones.puedeEscribir;
  const nombre = separarEmoji(category.name);

  const onGuardar = async () => {
    if (ejemplo || !nuevo || !esObjetivoConFecha(nuevo) || !puedeGuardar) {
      return;
    }
    setOcupado(true);
    try {
      const abrirDesde = carta.abrir === 'hasta' ? carta.abrirDesde : null;
      const cambiaMeta = metaCambiada(objetivo, nuevo);
      const saldo = await leerCelda(`leftover-${category.id}`);
      let mensaje: string;
      if (navega && !cambiaMeta) {
        mensaje = t('Letter saved.');
      } else if (saldo > 0) {
        mensaje = t('You set sail with {{amount}} on board.', {
          amount: format(saldo, 'financial'),
        });
      } else {
        mensaje = t('Your voyage has set sail.');
      }
      if (cambiaMeta) {
        const ok = await guardar(lineasDeObjetivo(nuevo, hoy), '');
        if (!ok) {
          return;
        }
      }
      if (esFijo) {
        setFijo(category.id, false);
      }
      let rid: string | null = null;
      if (navega && !cambiaMeta && recuerdo) {
        rid = recuerdo.base.rid;
        await acciones.editarCarta(rid, {
          carta: carta.carta,
          siEntonces: carta.siEntonces,
          abrirDesde,
        });
      } else {
        rid = await acciones.zarpar({
          categoriaId: category.id,
          objetivo: nuevo,
          nombre: nombre.resto || category.name,
          emoji: nombre.emoji,
          saldo,
          gastado: await leerCelda(`sum-amount-${category.id}`),
          clase,
          carta: carta.carta,
          siEntonces: carta.siEntonces,
          abrirDesde,
        });
      }
      if (rid && compartido && carta.firmando) {
        await acciones.firmarTambien(rid, firmaLimpia(carta.firma));
      }
      dispatch(
        addNotification({
          notification: { type: 'message', message: mensaje, timeout: 4000 },
        }),
      );
      onCerrar();
    } finally {
      setOcupado(false);
    }
  };

  const titulo = navega ? t('Your voyage') : t('Set sail');

  return (
    <Hoja aria-label={titulo} data-testid="hoja-zarpar" onCerrar={onCerrar}>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 12,
          marginBottom: 4,
        }}
      >
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text
            role="heading"
            aria-level={2}
            style={{
              fontSize: 20,
              fontWeight: 800,
              letterSpacing: '-0.02em',
              color: color.fg,
            }}
          >
            {titulo}
          </Text>
          <Text
            style={{
              ...texto.secundario,
              color: color.fg2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {category.name}
          </Text>
        </View>
        <BotonCerrarHoja onPress={onCerrar} />
      </View>

      <View style={{ gap: 14, paddingTop: 10 }}>
        <Segmentado
          etiqueta={t('Voyage')}
          testId="zarpar-tipo"
          estirar
          alto={40}
          valor={formulario.tipo === 'anual' ? 'anual' : 'una-vez'}
          onChange={tipo => cambiar({ tipo })}
          opciones={[
            { valor: 'una-vez', texto: t('Once') },
            { valor: 'anual', texto: t('Every year') },
          ]}
        />
        <View style={{ flexDirection: 'row', gap: 10 }}>
          <View style={{ flex: 1, minWidth: 0 }}>
            <label htmlFor="zarpar-importe" style={estiloEtiqueta}>
              <Trans>I need</Trans>
            </label>
            <CampoImporte
              id="zarpar-importe"
              aria-label={t('I need')}
              data-testid="zarpar-importe"
              valor={
                formulario.importeTexto.trim()
                  ? (currencyToInteger(formulario.importeTexto) ?? 0)
                  : null
              }
              onCambiar={centimos =>
                cambiar({
                  importeTexto:
                    centimos == null ? '' : format.forEdit(centimos),
                })
              }
              style={{ ...estiloCampo, justifyContent: 'flex-start' }}
              estiloTexto={{ fontSize: 16, fontWeight: 600 }}
            />
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <label htmlFor="zarpar-fecha" style={estiloEtiqueta}>
              {formulario.tipo === 'anual' ? (
                <Trans>Needed by</Trans>
              ) : (
                <Trans>Needed for</Trans>
              )}
            </label>
            <input
              id="zarpar-fecha"
              data-testid="zarpar-fecha"
              type="date"
              value={formulario.fecha}
              min={`${month}-01`}
              onChange={e => cambiar({ fecha: e.target.value })}
              style={estiloCampo}
            />
          </View>
        </View>
        {fechaPasada && (
          <Text style={{ fontSize: 13, color: color.bad }}>
            <Trans>The date must be in this month or later.</Trans>
          </Text>
        )}

        {!navega && (
          <View>
            <Text style={estiloEtiqueta}>
              <Trans>What is it for?</Trans>
            </Text>
            <View
              role="group"
              aria-label={t('What is it for?')}
              style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}
            >
              {METAS.map(m => {
                const elegida = m.id === clase;
                return (
                  <Button
                    key={m.id}
                    variant="bare"
                    aria-pressed={elegida}
                    data-testid={`zarpar-clase-${m.id}`}
                    onPress={() => setClase(m.id)}
                    style={{
                      minHeight: TACTIL,
                      gap: 6,
                      padding: '2px 12px 2px 4px',
                      borderRadius: 999,
                      backgroundColor: elegida
                        ? color.accentSoft
                        : color.surface2,
                      border: `2px solid ${elegida ? color.accent : 'transparent'}`,
                      color: elegida ? color.accent : color.fg2,
                      fontSize: 13,
                      fontWeight: 700,
                    }}
                  >
                    <DibujoMeta meta={m.id} size={32} />
                    {nombreCofre(t, m.id)}
                  </Button>
                );
              })}
            </View>
          </View>
        )}

        <SeccionCarta
          valor={carta}
          onChange={cambios => setCarta(prev => ({ ...prev, ...cambios }))}
          compartido={compartido}
          firmas={(recuerdo?.firmas ?? []).filter(
            f => f.disp !== acciones.disp,
          )}
          minAbrir={hoy}
        />

        <Boton
          bloque
          data-testid="zarpar-guardar"
          isDisabled={!puedeGuardar}
          onPress={() => void onGuardar()}
        >
          {navega ? t('Save') : t('Set sail')}
        </Boton>
        {navega && onCerrarTravesia && (
          <Button
            variant="bare"
            data-testid="zarpar-cerrar-travesia"
            onPress={() => onCerrarTravesia(categoryId)}
            style={{
              minHeight: TACTIL,
              color: color.fg2,
              fontWeight: 700,
              fontSize: 14,
              backgroundColor: 'transparent',
            }}
          >
            <Trans>Close the voyage</Trans>
          </Button>
        )}
      </View>
    </Hoja>
  );
}
